import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../services/auth.service';
import { BotLinkRequest, TutorService } from '../../../services/tutor.service';

/**
 * Admin/manager decision dialog for a twin↔tutor link request, opened
 * directly from the notification bell (dashboard-header). Shows who
 * requested what and offers Approve / Reject with an optional note.
 *
 * Approval performs the real link server-side (attach_dozent). If the
 * target tutor is already linked to a DIFFERENT twin, a warning is shown
 * because approving swaps that link.
 *
 * Rights-limited users (the requesters) can open it too — read-only:
 * decision buttons are gated on `isClientWide` + `status === 'pending'`.
 */
@Component({
  selector: 'app-link-request-decision-dialog',
  templateUrl: './link-request-decision-dialog.component.html',
  styleUrl: './link-request-decision-dialog.component.scss',
})
export class LinkRequestDecisionDialogComponent implements OnInit {
  /** Either a full request object or just the id (then it is fetched). */
  @Input() requestId!: string;
  @Input() request?: BotLinkRequest | null;

  isClientWide = false;
  loading = false;
  busy = false;
  notFound = false;
  decisionNote = '';

  /** True when approving would replace the target's existing twin link. */
  swapWarning = false;

  constructor(
    protected dialogRef: NbDialogRef<LinkRequestDecisionDialogComponent>,
    private tutorService: TutorService,
    private auth: AuthService,
    private toastr: NbToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.isClientWide = this.auth.isClientWide();
    if (this.request) {
      this.afterLoad(this.request);
    } else if (this.requestId) {
      this.fetch();
    } else {
      this.notFound = true;
    }
  }

  get isPending(): boolean {
    return this.request?.status === 'pending';
  }

  get canDecide(): boolean {
    return this.isClientWide && this.isPending;
  }

  private fetch(): void {
    this.loading = true;
    this.tutorService.getLinkRequest(this.requestId).subscribe({
      next: (r) => {
        this.loading = false;
        this.afterLoad(r);
      },
      error: () => {
        this.loading = false;
        this.notFound = true;
      },
    });
  }

  private afterLoad(r: BotLinkRequest): void {
    this.request = r;
    // Only admins acting on a pending request need the swap probe.
    if (this.isClientWide && r.status === 'pending' && r.target_tutor_bot_id) {
      this.tutorService.getAttachedDozent(r.target_tutor_bot_id).subscribe({
        next: (attached) => {
          this.swapWarning =
            !!attached && attached.dozent_bot_id !== r.dozent_bot_id;
        },
        error: () => {
          this.swapWarning = false;
        },
      });
    }
  }

  approve(): void {
    if (!this.request || this.busy) return;
    this.busy = true;
    this.tutorService
      .approveLinkRequest(this.request.id, this.decisionNote?.trim() || null)
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translate.instant('dialogs.linkRequest.approvedToast'),
            ''
          );
          this.dialogRef.close('approved');
        },
        error: (err) => {
          this.busy = false;
          this.toastr.danger(this.decisionError(err), '');
        },
      });
  }

  reject(): void {
    if (!this.request || this.busy) return;
    this.busy = true;
    this.tutorService
      .rejectLinkRequest(this.request.id, this.decisionNote?.trim() || null)
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translate.instant('dialogs.linkRequest.rejectedToast'),
            ''
          );
          this.dialogRef.close('rejected');
        },
        error: (err) => {
          this.busy = false;
          this.toastr.danger(this.decisionError(err), '');
        },
      });
  }

  private decisionError(err: any): string {
    const code = err?.error?.detail;
    const key =
      code === 'request_not_pending'
        ? 'dialogs.linkRequest.errNotPending'
        : 'dialogs.linkRequest.errGeneric';
    return this.translate.instant(key);
  }

  templateLabelKey(templateName?: string | null): string {
    return `tutor.twinLinks.tpl.${templateName || 'unknown'}`;
  }

  statusKey(status?: string): string {
    return `tutor.twinLinks.status.${status || 'pending'}`;
  }
}
